import React, { useState, useEffect, useReducer, useCallback, useContext, useMemo } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment-timezone";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import { useTimezone } from "../../hooks/useTimezone";

import "./Schedules.css"; // Importe o arquivo CSS

// Defina a função getUrlParam antes de usá-la
function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const eventTitleStyle = {
  fontSize: "14px", // Defina um tamanho de fonte menor
  overflow: "hidden", // Oculte qualquer conteúdo excedente
  whiteSpace: "nowrap", // Evite a quebra de linha do texto
  textOverflow: "ellipsis", // Exiba "..." se o texto for muito longo
};

const localizer = momentLocalizer(moment);

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    // Si es la primera página, reemplazar completamente
    if (action.payload.pageNumber === 1) {
      return action.payload.schedules;
    }
    // Si es paginación, concatenar
    return [...state, ...action.payload.schedules];
  }

  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const scheduleIndex = state.findIndex((s) => s.id === schedule.id);

    if (scheduleIndex !== -1) {
      state[scheduleIndex] = schedule;
      return [...state];
    } else {
      return [schedule, ...state];
    }
  }

  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;
    return state.filter((s) => s.id !== scheduleId);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    borderRadius: 14,
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#e5e8f0"}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
}));

const Schedules = () => {
  const classes = useStyles();

  const { user } = useContext(AuthContext);
  const { parseNaiveDate, formatDate, formatTime, getConfiguredTimezone } = useTimezone();
  const timezone = getConfiguredTimezone();

  const calendarMessages = useMemo(
    () => ({
      date: i18n.t("schedulePage.calendar.date"),
      time: i18n.t("schedulePage.calendar.time"),
      event: i18n.t("schedulePage.calendar.event"),
      allDay: i18n.t("schedulePage.calendar.allDay"),
      week: i18n.t("schedulePage.calendar.week"),
      work_week: i18n.t("schedulePage.calendar.work_week"),
      day: i18n.t("schedulePage.calendar.day"),
      month: i18n.t("schedulePage.calendar.month"),
      previous: i18n.t("schedulePage.calendar.previous"),
      next: i18n.t("schedulePage.calendar.next"),
      yesterday: i18n.t("schedulePage.calendar.yesterday"),
      tomorrow: i18n.t("schedulePage.calendar.tomorrow"),
      today: i18n.t("schedulePage.calendar.today"),
      agenda: i18n.t("schedulePage.calendar.agenda"),
      noEventsInRange: i18n.t("schedulePage.calendar.noEventsInRange"),
      showMore: (total) => i18n.t("schedulePage.calendar.showMore", { total }),
    }),
    [i18n.language],
  );

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));
  const [viewMode, setViewMode] = useState("calendar"); // "calendar" o "table"


  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules/", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_SCHEDULES", payload: { schedules: data.schedules, pageNumber } });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) {
      handleOpenScheduleModal();
    }
  }, [contactId]);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [
    searchParam,
    pageNumber,
    contactId,
    fetchSchedules,
    handleOpenScheduleModalFromContactId,
  ]);

  useEffect(() => {
    handleOpenScheduleModalFromContactId();
    const socket = socketManager.getSocket(user.companyId);

    // ✅ CORRECCIÓN: Escuchar el evento 'schedule' que se emite en 'company-{companyId}-mainchannel'
    socket.on("schedule", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
    });

    return () => {
      // ✅ CORRECCIÓN: Solo remover el listener específico, no desconectar todo el socket
      socket.off("schedule");
    };
  }, [handleOpenScheduleModalFromContactId, socketManager, user]);

  const cleanContact = () => {
    setContactId("");
  };

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      // Buscar el schedule para verificar su estado
      const schedule = schedules.find(s => s.id === scheduleId);
      
      if (schedule && schedule.isReminderSystem) {
        // Si es del sistema de recordatorios, usar la lógica de cancelar
        await api.post(`/schedules/${scheduleId}/cancel-reminder`);
        toast.success(i18n.t("schedulePage.toastMeetingCancelled"));
      } else {
        // Cancelar agendamiento normal (sin eliminar)
        await api.post(`/schedules/${scheduleId}/cancel`);
        toast.success(i18n.t("schedulePage.toastScheduleCancelled"));
      }
      
      setDeletingSchedule(null);
      setSearchParam("");
      setPageNumber(1);

      dispatch({ type: "RESET" });
      setPageNumber(1);
      await fetchSchedules();
      
      // Forzar actualización de página para eliminar residuos
      window.location.reload();
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const truncate = (str, len) => {
    if (str.length > len) {
      return str.substring(0, len) + "...";
    }
    return str;
  };

  const getScheduleStatus = (schedule) => {
    // ✅ CORRECCIÓN: Usar timezone de empresa para comparar fechas
    const now = moment.tz(timezone);
    const scheduleMoment = parseNaiveDate(schedule.sendAt);
    
    if (!scheduleMoment) {
      return { text: i18n.t("schedulePage.status.error"), color: "red" };
    }
    
    // Verificar estado específico del agendamiento
    if (schedule.status === "CANCELADO") {
      return { text: i18n.t("schedulePage.status.cancelled"), color: "gray" };
    } else if (schedule.sentAt) {
      return { text: i18n.t("schedulePage.status.sent"), color: "green" };
    } else if (scheduleMoment.isBefore(now)) {
      return { text: i18n.t("schedulePage.status.overdue"), color: "red" };
    } else {
      return { text: i18n.t("schedulePage.status.pending"), color: "orange" };
    }
  };
  
  // ✅ HELPER: Obtener fecha parseada correctamente para un schedule
  const getScheduleMoment = (schedule) => {
    return parseNaiveDate(schedule.sendAt);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingSchedule &&
          (deletingSchedule.isReminderSystem
            ? i18n.t("schedulePage.cancelMeetingTitle")
            : i18n.t("schedulePage.cancelScheduleTitle"))
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {deletingSchedule && deletingSchedule.isReminderSystem
          ? i18n.t("schedulePage.cancelMeetingMessage")
          : i18n.t("schedulePage.cancelScheduleMessage")}
      </ConfirmationModal>
      <ScheduleModal
        open={scheduleModalOpen}
        onClose={handleCloseScheduleModal}
        reload={fetchSchedules}
        aria-labelledby="form-dialog-title"
        scheduleId={selectedSchedule && selectedSchedule.id}
        contactId={contactId}
        cleanContact={cleanContact}
      />
      <MainHeader>
        <div>
          <Title>{i18n.t("schedulePage.moduleTitle")}</Title>
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: -6 }}>
            {i18n.t("schedulePage.scheduledTotal", { count: schedules.length })}
          </Typography>
        </div>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setViewMode(viewMode === "calendar" ? "table" : "calendar")}
            style={{ marginRight: 8 }}
          >
            {viewMode === "calendar" ? i18n.t("schedulePage.viewTable") : i18n.t("schedulePage.viewCalendar")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenScheduleModal}
          >
            {i18n.t("schedulePage.newButton")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        {viewMode === "calendar" ? (
          <Calendar
            key={i18n.language}
            culture={(i18n.language || "es").split("-")[0]}
            messages={calendarMessages}
            formats={{
            agendaDateFormat: "DD/MM ddd",
            weekdayFormat: "dddd"
        }}
            localizer={localizer}
            events={schedules.map((schedule) => {
              const scheduleMoment = getScheduleMoment(schedule);
              const now = moment.tz(timezone);
              const isPast = scheduleMoment ? scheduleMoment.isBefore(now) : false;
              
              return {
                title: (
                  <div className="event-container">
                    <div style={eventTitleStyle}>{schedule.contact?.name || i18n.t("schedulePage.notAvailable")}</div>
                    <DeleteOutlineIcon
                      onClick={() => {
                        setDeletingSchedule(schedule);
                        setConfirmModalOpen(true);
                      }}
                      className="delete-icon"
                      style={{ 
                        opacity: (schedule.sentAt || isPast) ? 0.3 : 1,
                        cursor: (schedule.sentAt || isPast) ? 'not-allowed' : 'pointer'
                      }}
                    />
                    <EditIcon
                      onClick={() => {
                        handleEditSchedule(schedule);
                        setScheduleModalOpen(true);
                      }}
                      className="edit-icon"
                      style={{ 
                        opacity: isPast ? 0.3 : 1,
                        cursor: isPast ? 'not-allowed' : 'pointer'
                      }}
                    />
                  </div>
                ),
                // ✅ CORRECCIÓN: Usar fecha parseada correctamente para el calendario
                start: scheduleMoment ? scheduleMoment.toDate() : new Date(schedule.sendAt),
                end: scheduleMoment ? scheduleMoment.toDate() : new Date(schedule.sendAt),
              };
            })}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 500 }}
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">{i18n.t("schedulePage.table.date")}</TableCell>
                <TableCell align="center">{i18n.t("schedulePage.table.time")}</TableCell>
                <TableCell align="center">{i18n.t("schedulePage.table.contact")}</TableCell>
                <TableCell align="center">{i18n.t("schedulePage.table.connection")}</TableCell>
                <TableCell align="center">{i18n.t("schedulePage.table.message")}</TableCell>
                <TableCell align="center">{i18n.t("schedulePage.table.status")}</TableCell>
                <TableCell align="center">{i18n.t("schedulePage.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((schedule) => {
                const status = getScheduleStatus(schedule);
                const scheduleMoment = getScheduleMoment(schedule);
                const now = moment.tz(timezone);
                const isPast = scheduleMoment ? scheduleMoment.isBefore(now) : false;
                
                return (
                  <TableRow key={schedule.id}>
                    <TableCell align="center">
                      {scheduleMoment ? scheduleMoment.format('DD/MM/YYYY') : i18n.t("schedulePage.notAvailable")}
                    </TableCell>
                    <TableCell align="center">
                      {scheduleMoment ? scheduleMoment.format('HH:mm') : i18n.t("schedulePage.notAvailable")}
                    </TableCell>
                    <TableCell align="center">
                      {schedule.contact?.name || i18n.t("schedulePage.notAvailable")}
                    </TableCell>
                    <TableCell align="center">
                      {schedule.whatsapp?.name || i18n.t("schedulePage.notAvailable")}
                    </TableCell>
                    <TableCell align="center">
                      {truncate(schedule.body, 50)}
                    </TableCell>
                    <TableCell align="center">
                      <span style={{ 
                        color: status.color, 
                        fontWeight: 'bold',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: status.color === 'green' ? '#e8f5e8' : 
                                       status.color === 'red' ? '#ffe8e8' : '#fff3e0'
                      }}>
                        {status.text}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleEditSchedule(schedule)}
                        disabled={Boolean(isPast || schedule.status === "CANCELADO")}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setDeletingSchedule(schedule);
                          setConfirmModalOpen(true);
                        }}
                        disabled={Boolean(schedule.sentAt || isPast || schedule.status === "CANCELADO")}
                        size="small"
                        title={schedule.status === "CANCELADO" ? i18n.t("schedulePage.deleteTitleAlreadyCancelled") : i18n.t("schedulePage.deleteTitleCancel")}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Schedules;